import React, { useState } from "react";
import { CurvedTopHeader } from "./SelectYourPlan";
import TextBox from "../../components/Common/TextBox";
import TextArea from "../../components/Common/TextArea";
import {
  getLocalStorage,
  setLocalStorage,
  trimString,
  formatPhoneNumber,
} from "../../helpers/common";
import {
  nameValidation,
  licenseValidation,
  phoneNumberValidation,
} from "../../helpers/validations";
import {
  USER_INFO_KEY,
} from "../../constants/common";
import { Button } from "react-bootstrap";
import { pageURLs } from "@/constants";
import useUserDetailsById from "../../hooks/useUserDetailsById";
import { loggedInUser } from "../../helpers/common";
import { useNavigate } from "react-router-dom";
import { saveAgentInfo } from "../../services/v2/signupService";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { toast } from "react-toastify";
import useLoading from "@/hooks/useLoading";

const FieldWrapper = ({ label, children }) => (
  <div className="textbox-wrap mb-3 mb-sm-3 agent-info-textfield">
    <label className="agent-modal-label">
      {label} <span>*</span>
    </label>
    {children}
  </div>
);

const FirstNameField = React.memo(({ value, error, onChange }) => (
  <FieldWrapper label="First name">
    <TextBox
      id="fName"
      name="firstName"
      type="text"
      maxLength={30}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="First name"
      className="form-control"
      errorClass="error-validation-msg"
      errorMessage={error}
    />
  </FieldWrapper>
));

export const LastNameField = React.memo(({ value, error, onChange }) => (
  <FieldWrapper label="Last name">
    <TextBox
      id="lName"
      name="lastName"
      maxLength={30}
      value={value}
      type="text"
      placeholder="Last name"
      className="form-control"
      onChange={(e) => onChange(e.target.value)}
      errorClass="error-validation-msg"
      errorMessage={error}
    />
  </FieldWrapper>
));

export const PhoneNumberField = React.memo(
  ({ value, error, onChange, onBlur }) => (
    <FieldWrapper label="Phone Number">
      <TextBox
        id="phone"
        name="phone"
        type="text"
        maxLength={14}
        value={value}
        className="form-control"
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        errorClass="error-validation-msg"
        errorMessage={error}
        placeholder="Phone number"
      />
    </FieldWrapper>
  ),
);

export const LocationField = React.memo(
  ({ value, error, onChange, list = [], onSelect }) => (
    <FieldWrapper label="Location">
      <div className="position-relative">
        <TextBox
          id="location"
          name="location"
          type="text"
          maxLength={40}
          value={value}
          className="form-control"
          onChange={(e) => onChange(e.target.value)}
          errorClass="error-validation-msg"
          errorMessage={error}
          placeholder="City, State"
        />
        {list.length > 0 && (
          <div className="location-result-wrap mw-100 mt-4">
            <ul>
              {list.map((address, i) => (
                <li key={i}>
                  <a
                    role="button"
                    href={void 0}
                    onClick={() => onSelect(address)}
                  >
                    {formatAddressForDisplay(_.get(address, "formatted_address", ""))}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </FieldWrapper>
  ),
);

export const CompanyField = React.memo(({ value, error, onChange }) => (
  <FieldWrapper label="Company">
    <TextBox
      id="company"
      name="company"
      type="text"
      maxLength={40}
      value={value}
      className="form-control"
      onChange={(e) => onChange(e.target.value)}
      errorClass="error-validation-msg"
      errorMessage={error}
      placeholder="Company name"
    />
  </FieldWrapper>
));

export const LicenseNumberField = React.memo(({ value, error, onChange }) => (
  <FieldWrapper label="License Number">
    <TextBox
      id="license"
      name="license"
      type="text"
      maxLength={40}
      value={value}
      className="form-control"
      onChange={(e) => onChange(e.target.value)}
      errorClass="error-validation-msg"
      errorMessage={error}
      placeholder="License Number"
    />
  </FieldWrapper>
));

const LOADING_KEY = 'complete-your-profile-default-submit';

const CompleteYourProfileDefault = () => {

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState();
  const [company, setCompany] = useState();
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [phone, setPhone] = useState();
  const [license, setLicense] = useState();
  const [phoneError, setPhoneError] = useState("");
  const [licenseError, setLicenseError] = useState("");

  const { startLoading, stopLoading } = useLoading();

  const { isGitchaDefaultWebsite } = useCurrentWebsite();

  const user = loggedInUser();
  const userDetails = useUserDetailsById(user?.userInfo?.userId);

  const isPremiumUser =
    !!userDetails?.PlanName && userDetails?.PlanName !== "Lite";

  const navigate = useNavigate();

  const handleSubmit = async () => {
    startLoading(LOADING_KEY);
    const savedUserInfo = getLocalStorage(USER_INFO_KEY);
    const savedLocation = savedUserInfo?.userInfo?.location || "";
    const agentInfo = {
      FirstName: firstName,
      LastName: lastName,
      Company: company,
      Location: savedLocation,
      LicenseNo: license,
      Phone: phone,
    };
    try {
      await saveAgentInfo(agentInfo).then((response) => {
        if (response) {
          if (response.errorCode === 0) {
            const _userDetails = getLocalStorage(USER_INFO_KEY);
            if (_userDetails) {
              _userDetails.userInfo.userStatus = true;
              _userDetails.userInfo.firstName = firstName;
              _userDetails.userInfo.company = company;
              _userDetails.userInfo.lastName = lastName;
              _userDetails.userInfo.name = firstName + " " + lastName;
            }
            setLocalStorage(USER_INFO_KEY, _userDetails);

            stopLoading(LOADING_KEY);
            if (isPremiumUser) {
              navigate(pageURLs.completeYourProfilePremium);
            } else {
              const url = `${pageURLs.home}`.concat(isGitchaDefaultWebsite ? "" : "?showAgentModal=1");
              navigate(url);
            }
          } else {
            stopLoading(LOADING_KEY);
            console.log("Something went wrong");
          }
        } else {
          stopLoading(LOADING_KEY);
        }
      });
    } catch (e) {
      stopLoading(LOADING_KEY);
      toast.error('Something went wrong while completing your profile. Please try again later.');
    }
  };

  const checkValid = () =>
    [firstName, lastName, company, phone, license].every((v) =>
      (v ?? "").trim(),
    ) &&
    [
      firstNameError,
      lastNameError,
      companyError,
      phoneError,
      licenseError,
    ].every((e) => !(e ?? "").trim()) &&
    phoneNumberValidation(phone ?? "");

  const isValid = checkValid();

  /**
   * To validate phone number
   * @param {*} name
   */
  const validatePhoneNumber = () => {
    setPhoneError("");
    if (trimString(phone) !== "") {
      if (!phoneNumberValidation(phone.trim()) || phone === "000-000-0000") {
        setPhoneError("Enter Valid Phone Number");
      }
    } else {
      setPhoneError("Enter Phone Number");
      setPhone("");
    }
  };

  /**
   * To handle license field changes
   * @param {*} name
   */
  const handleLicenseChange = (name) => {
    setLicenseError("");
    setLicense(name);
    if (name.trim() === "") {
      setLicenseError("Enter License Number");
    } else if (!licenseValidation(name.trim())) {
      //V2 change for license validation
      setLicenseError("Invalid input");
    }
  };

  const handleFirstName = (name) => {
    setFirstNameError("");
    setFirstName(name);
    if (name.trim() === "") {
      setFirstNameError("Enter First Name");
    } else if (!nameValidation(name.trim())) {
      setFirstNameError("Invalid input");
    }
  };

  const handleLastName = (name) => {
    setLastNameError("");
    setLastName(name);
    if (name.trim() === "") {
      setLastNameError("Enter Last Name");
    } else if (!nameValidation(name.trim())) {
      setLastNameError("Invalid input");
    }
  };

  const handlePhone = (value) => {
    setPhoneError("");
    if (trimString(value) !== "") {
      value = formatPhoneNumber(value);
      setPhone(value);
    } else {
      setPhoneError("Enter Phone Number");
      setPhone("");
    }
  };

  const handleCompany = (name) => {
    setCompanyError("");
    setCompany(name);
    if (name.trim() === "") {
      setCompanyError("Enter Company");
    } else if (
      !/^[a-zA-Z0-9\s&]+$/.test(name.trim()) ||
      /^[0-9&]+$/.test(name.trim())
    ) {
      setCompanyError("Invalid input");
    }
  };

  return (
    <>
      <CurvedTopHeader />

      <div className="complete-profile-container">
        <h1 className="complete-profile-heading">Tell us about yourself:</h1>

        <div className="complete-profile-form">
          <div className="complete-form-row">
            <FirstNameField
              value={firstName}
              error={firstNameError}
              onChange={handleFirstName}
            />
            <LastNameField
              value={lastName}
              error={lastNameError}
              onChange={handleLastName}
            />
          </div>
          <PhoneNumberField
            value={phone}
            error={phoneError}
            onChange={handlePhone}
            onBlur={validatePhoneNumber}
          />
          <CompanyField
            value={company}
            error={companyError}
            onChange={handleCompany}
          />
          <LicenseNumberField
            value={license}
            error={licenseError}
            onChange={handleLicenseChange}
          />
          <Button
            type="button"
            variant="primary"
            disabled={!isValid}
            // className="btn-continue"
            onClick={handleSubmit}
          >
            Continue
          </Button>
        </div>
      </div>
    </>
  );
};

export default CompleteYourProfileDefault;
